/* PURE STUDIOS — commerce: size picker, bag drawer, cart page, contact draft. */
(() => {
  'use strict';

  const PS = window.PureStudios || {};
  const routes = Object.assign({ root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', collections: '/collections/all' }, PS.routes);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ------------------------------------------------------------ money */
  // Mirrors Shopify's money formats so prices rendered here match prices rendered in Liquid.
  function formatMoney(cents, format = PS.moneyFormat || '${{amount}}') {
    const value = Number(cents) || 0;
    const group = (n, decimals, thousands = ',', dec = '.') => {
      const [whole, frac] = (n / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? dec + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
      amount: group(value, 2),
      amount_no_decimals: group(value, 0),
      amount_with_comma_separator: group(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: group(value, 0, '.', ','),
      amount_with_apostrophe_separator: group(value, 2, "'", '.'),
      amount_with_space_separator: group(value, 2, ' ', ','),
    })[key] ?? group(value, 2));
  }
  const escapeHTML = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const sized = (url, width) => !url ? '' : url + (url.includes('?') ? '&' : '?') + 'width=' + width;

  /* ------------------------------------------------------------ feedback */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }

  /* ------------------------------------------------------------ cart API */
  async function cartRequest(url, body) {
    const encoded = body instanceof URLSearchParams;
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: Object.assign({ Accept: 'application/json' }, body && !encoded ? { 'Content-Type': 'application/json' } : {}),
      body: body ? (encoded ? body : JSON.stringify(body)) : undefined,
      credentials: 'same-origin',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }
  const getCart = () => cartRequest(`${routes.cart}.js`);

  function updateCounts(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
    $$('[data-bag-open]').forEach((el) => {
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  /* ------------------------------------------------------------ bag drawer */
  const drawer = $('[data-cart-drawer]');
  let lastCart = null;

  function lineHTML(item, index) {
    const options = item.product_has_only_default_variant ? '' :
      (item.options_with_values || []).map((o) => `${escapeHTML(o.name)}: ${escapeHTML(o.value)}`).join(' · ');
    return `
      <li class="cart-line" data-line="${index + 1}" data-key="${escapeHTML(item.key)}">
        <a class="cart-line__media" href="${escapeHTML(item.url)}" tabindex="-1" aria-hidden="true">
          ${item.image ? `<img src="${escapeHTML(sized(item.image, 200))}" alt="" width="76" height="95" loading="lazy">` : ''}
        </a>
        <div class="cart-line__info">
          <a class="cart-line__title" href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>
          ${options ? `<p class="cart-line__meta">${options}</p>` : ''}
          <div class="cart-line__actions">
            <div class="quantity quantity--small">
              <button type="button" data-drawer-step="-1" aria-label="Decrease quantity of ${escapeHTML(item.product_title)}">&minus;</button>
              <input type="number" value="${item.quantity}" min="0" inputmode="numeric" aria-label="Quantity of ${escapeHTML(item.product_title)}" data-drawer-qty>
              <button type="button" data-drawer-step="1" aria-label="Increase quantity of ${escapeHTML(item.product_title)}">+</button>
            </div>
            <button class="link-quiet" type="button" data-drawer-remove>Remove</button>
          </div>
        </div>
        <p class="cart-line__total">${formatMoney(item.final_line_price)}</p>
      </li>`;
  }

  function renderDrawer(cart) {
    if (!drawer) return;
    lastCart = cart;
    const body = $('[data-drawer-body]', drawer);
    const foot = $('[data-drawer-foot]', drawer);
    if (!cart.item_count) {
      body.innerHTML = `
        <div class="drawer__empty">
          <p class="empty-state__title">Your bag is empty</p>
          <p class="hand-note">Nothing picked out yet.</p>
          <a class="btn btn--outline" href="${escapeHTML(routes.collections || '/collections/all')}">Shop the collection</a>
        </div>`;
      foot.hidden = true;
    } else {
      body.innerHTML = `<ul class="cart-lines" role="list">${cart.items.map(lineHTML).join('')}</ul>`;
      $('[data-drawer-total]', drawer).textContent = formatMoney(cart.total_price);
      foot.hidden = false;
    }
    updateCounts(cart.item_count);
  }

  function openDrawer(opener) {
    if (!drawer) return false;
    drawer._opener = opener || document.activeElement;
    if (!drawer.open) drawer.showModal();
    $('[data-drawer-close]', drawer).focus({ preventScroll: true });
    return true;
  }
  function closeDrawer() { if (drawer?.open) drawer.close(); }

  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer || e.target.closest('[data-drawer-close]')) closeDrawer();
    });
    drawer.addEventListener('close', () => { drawer._opener?.focus?.({ preventScroll: true }); });

    let pending = Promise.resolve();
    const changeLine = (li, quantity) => {
      li.classList.add('is-busy');
      pending = pending.then(() => cartRequest(`${routes.cartChange}.js`, { id: li.dataset.key, quantity }))
        .then(renderDrawer)
        .catch((err) => { li.classList.remove('is-busy'); toast(err.message); });
    };
    drawer.addEventListener('click', (e) => {
      const step = e.target.closest('[data-drawer-step]');
      const remove = e.target.closest('[data-drawer-remove]');
      const li = e.target.closest('.cart-line');
      if (!li) return;
      if (step) {
        const input = $('[data-drawer-qty]', li);
        changeLine(li, Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.drawerStep)));
      } else if (remove) changeLine(li, 0);
    });
    drawer.addEventListener('change', (e) => {
      if (!e.target.matches('[data-drawer-qty]')) return;
      changeLine(e.target.closest('.cart-line'), Math.max(0, parseInt(e.target.value, 10) || 0));
    });
  }

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-bag-open]');
    if (!link || !drawer || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    openDrawer(link);
    getCart().then(renderDrawer).catch(() => { window.location.href = link.href; });
  });

  /* ------------------------------------------------------------ product form */
  function initProduct(section) {
    if (section.dataset.bound) return;
    section.dataset.bound = 'true';
    const form = $('.product-form', section);
    const dataEl = $('[data-product-json]', section);
    if (!form) return;
    const data = dataEl ? JSON.parse(dataEl.textContent) : { variants: [] };
    const idInput = $('[data-variant-id]', form);
    const button = $('[data-add-button]', form);
    const label = $('[data-add-label]', form);
    const message = $('[data-form-message]', form);
    const picker = $('[data-variant-picker]', form);

    const selected = () => $$('.option', picker).map((fs) => $('.option__input:checked', fs)?.value ?? null);
    const findVariant = (opts) => data.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    function refreshAvailability(opts) {
      $$('.option', picker).forEach((fs, index) => {
        $$('.option__input', fs).forEach((input) => {
          const trial = opts.slice(); trial[index] = input.value;
          const match = data.variants.find((v) => v.options.every((o, i) => o === trial[i]));
          const pill = input.nextElementSibling;
          const available = Boolean(match && match.available);
          pill.classList.toggle('is-unavailable', !available);
          const sr = $('.visually-hidden', pill);
          if (!available && !sr) pill.insertAdjacentHTML('beforeend', '<span class="visually-hidden"> — sold out</span>');
          if (available && sr) sr.remove();
        });
        const checked = $('.option__input:checked', fs);
        const valueEl = $('[data-option-value]', fs);
        if (valueEl && checked) valueEl.textContent = checked.value;
      });
    }

    function applyVariant(variant) {
      message.hidden = true;
      if (!variant) {
        button.disabled = true;
        label.textContent = 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      button.disabled = !variant.available;
      label.textContent = variant.available ? 'Add to bag' : 'Sold out';
      const price = $('[data-price]', section);
      const compare = $('[data-compare-price]', section);
      if (price) price.textContent = formatMoney(variant.price);
      if (compare) {
        const onSale = variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        price.closest('.price')?.classList.toggle('price--sale', onSale);
      }
      if (data.url && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    if (picker) {
      picker.addEventListener('change', () => {
        const opts = selected();
        refreshAvailability(opts);
        applyVariant(findVariant(opts));
      });
      refreshAvailability(selected());
    }

    // Quantity stepper
    form.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step) return;
      const input = $('input', step.parentElement);
      const min = Number(input.min || 1), max = input.max ? Number(input.max) : Infinity;
      input.value = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep)));
    });

    // Add to bag without leaving the page
    form.addEventListener('submit', async (e) => {
      if (!drawer) return;
      e.preventDefault();
      if (button.disabled) return;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      message.hidden = true;
      try {
        await cartRequest(`${routes.cartAdd}.js`, new URLSearchParams(new FormData(form)));
        const cart = await getCart();
        renderDrawer(cart);
        openDrawer(button);
      } catch (err) {
        message.textContent = err.message;
        message.hidden = false;
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    });
  }

  /* ------------------------------------------------------------ cart page */
  function initCartPage(root) {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';
    const sectionId = root.dataset.sectionId;

    async function refresh(focusKey) {
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin' });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = $('[data-cart-page]', doc);
      if (!next) return window.location.reload();
      root.innerHTML = next.innerHTML;
      if (focusKey) $(`[data-line-key="${CSS.escape(focusKey)}"] input`, root)?.focus();
    }

    let queue = Promise.resolve();
    const change = (li, quantity) => {
      const key = li.dataset.lineKey;
      li.classList.add('is-busy');
      queue = queue.then(() => cartRequest(`${routes.cartChange}.js`, { id: key, quantity }))
        .then((cart) => { updateCounts(cart.item_count); return refresh(quantity > 0 ? key : null); })
        .catch((err) => { li.classList.remove('is-busy'); const m = $('[data-form-message]', root); if (m) { m.textContent = err.message; m.hidden = false; } });
    };

    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-line-step]');
      const remove = e.target.closest('[data-line-remove]');
      const li = e.target.closest('[data-line-key]');
      if (!li) return;
      if (step) {
        const input = $('input[name="updates[]"]', li);
        change(li, Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.lineStep)));
      } else if (remove) {
        e.preventDefault();
        change(li, 0);
      }
    });
    root.addEventListener('change', (e) => {
      if (e.target.matches('input[name="updates[]"]')) change(e.target.closest('[data-line-key]'), Math.max(0, parseInt(e.target.value, 10) || 0));
    });
    // Keep the order note saved even if the shopper leaves without checking out.
    let noteTimer;
    root.addEventListener('input', (e) => {
      if (!e.target.matches('[data-cart-note]')) return;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => cartRequest(`${routes.cart}/update.js`, { note: e.target.value }).catch(() => {}), 500);
    });
  }

  /* ------------------------------------------------------------ small things */
  document.addEventListener('change', (e) => {
    const form = e.target.closest('form[data-auto-submit]');
    if (form && e.target.matches('select')) form.submit();
  });

  // Contact: validate, then open a prefilled email draft.
  function contactErrors(v) {
    const errors = {};
    if (!v.name.trim()) errors.name = 'Please add your name.';
    if (!v.email.trim()) errors.email = 'Please add your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) errors.email = 'That email doesn’t look right.';
    if (!v.message.trim()) errors.message = 'Please write a note.';
    return errors;
  }
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-contact-draft]');
    if (!form) return;
    e.preventDefault();
    const values = { name: form.elements.name.value, email: form.elements.email.value, message: form.elements.message.value };
    const errors = contactErrors(values);
    let first = null;
    for (const field of ['name', 'email', 'message']) {
      const el = $(`[data-error="${field}"]`, form);
      el.textContent = errors[field] || '';
      el.hidden = !errors[field];
      form.elements[field].setAttribute('aria-invalid', String(Boolean(errors[field])));
      if (errors[field] && !first) first = form.elements[field];
    }
    if (first) { first.focus(); return; }
    const body = `Name: ${values.name.trim()}\nEmail: ${values.email.trim()}\n\n${values.message.trim()}`;
    window.location.href = `mailto:${form.dataset.recipient}?subject=${encodeURIComponent(form.dataset.subject)}&body=${encodeURIComponent(body)}`;
    const status = $('[data-contact-status]', form);
    if (status) status.textContent = 'Your email app should open with the message ready to send. Nothing opened? Write to us at the address above.';
  });

  function init(root = document) {
    $$('[data-product-section]', root).forEach(initProduct);
    $$('[data-cart-page]', root).forEach(initCartPage);
  }
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.PureStudios = Object.assign(PS, { formatMoney, openDrawer, renderDrawer, getCart });
})();
